import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';

import { customerService } from '../../../services/api';
import BoundedPageLayout from '../../../components/dashboard/BoundedPageLayout';
import PageHeader from '../../../components/dashboard/PageHeader';
import ListFilterToolbar from '../../../components/dashboard/ListFilterToolbar';
import RowActionsMenu from '../../../components/dashboard/RowActionsMenu';
import SuccessSnackbar from '../../../components/dashboard/SuccessSnackbar';
import UndoSnackbar from '../../../components/dashboard/UndoSnackbar';
import useUndoDelete from '../../../components/dashboard/useUndoDelete';
import DataGridCard from '../../../components/dashboard/DataGridCard';
import { buildDepartmentColumns } from './columns';
import { formatDate } from '../../../utils/formatters';

/**
 * Unlike `/roles/`, `/departments/` keeps DRF pagination on, so the response
 * is a `{count, next, previous, results}` envelope rather than a bare array.
 * PAGE_SIZE is 100 and departments are a handful per customer, so the page
 * still filters/paginates client-side — it just has to unwrap first.
 */
const unwrapResults = (data) => {
  if (Array.isArray(data)) return data;
  return data?.results ?? [];
};

export default function DepartmentsList() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [selectionModel, setSelectionModel] = useState([]);
  const [showColumnBorder, setShowColumnBorder] = useState(false);
  const [showCellBorder, setShowCellBorder] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['departments'],
    queryFn: () => customerService.getDepartments().then((res) => unwrapResults(res.data)),
  });

  const departments = useMemo(() => data ?? [], [data]);

  const matchesFilters = useCallback((department, { searchValue }) => {
    if (!searchValue) return true;
    const needle = searchValue.toLowerCase();
    return (
      department.name.toLowerCase().includes(needle) ||
      (department.internal_name ?? '').toLowerCase().includes(needle)
    );
  }, []);

  const filteredDepartments = useMemo(
    () => departments.filter((department) => matchesFilters(department, { searchValue: search })),
    [departments, search, matchesFilters],
  );

  const applySelection = useCallback(
    (overrides) => {
      const visibleIds = departments
        .filter((department) =>
          matchesFilters(department, { searchValue: overrides.searchValue ?? search }),
        )
        .map((department) => department.uuid);
      setSelectionModel((prev) => prev.filter((id) => visibleIds.includes(id)));
    },
    [departments, search, matchesFilters],
  );

  const updateSearch = (value) => {
    setSearch(value);
    applySelection({ searchValue: value });
  };

  const clearFilters = () => {
    setSearch('');
    applySelection({ searchValue: '' });
  };

  const { pending, remove, undo, dismiss, seed } = useUndoDelete({
    onDelete: (uuid) =>
      customerService
        .deleteDepartment(uuid)
        .then(() => queryClient.invalidateQueries({ queryKey: ['departments'] })),
    onRestore: (uuid) =>
      customerService
        .restoreDepartment(uuid)
        .then(() => queryClient.invalidateQueries({ queryKey: ['departments'] })),
  });

  const [flashMessage, setFlashMessage] = useState('');

  useEffect(() => {
    const seedDelete = location.state?.seedDelete;
    if (seedDelete) {
      seed(seedDelete.ids, seedDelete.message);
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }
    if (location.state?.flashMessage) {
      setFlashMessage(location.state.flashMessage);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const handleDeleteOne = (department) => {
    remove(department.uuid, t('departments.deleteSuccess'));
    setSelectionModel((prev) => prev.filter((id) => id !== department.uuid));
  };

  const handleDeleteSelected = () => {
    const ids = [...selectionModel];
    remove(ids, t('departments.deleteSuccess'));
    setSelectionModel([]);
  };

  const columns = useMemo(
    () =>
      buildDepartmentColumns({
        t,
        language: i18n.language,
        onView: (department) => navigate(`/departments/${department.uuid}`),
        onEdit: (department) => navigate(`/departments/${department.uuid}/edit`),
        onDelete: handleDeleteOne,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language, navigate],
  );

  const header = (
    <PageHeader
      title={t('departments.listTitle')}
      breadcrumbs={[
        { label: t('departments.breadcrumb.dashboard') },
        { label: t('nav.groups.team') },
        { label: t('departments.breadcrumb.list') },
      ]}
      actions={
        <Button variant="contained" onClick={() => navigate('/departments/new')}>
          {t('departments.newDepartment')}
        </Button>
      }
    />
  );

  const selectedCount = selectionModel.length;

  // Plain props object, NOT a function/component: `slots.toolbar` must stay a
  // stable reference (the imported ListFilterToolbar) across renders. Handing
  // DataGrid a freshly-defined render function every render — even one that
  // just wraps the same component — makes React treat it as a different
  // component type each time, remounting the toolbar (and its focused search
  // input) after every keystroke.
  //
  // Departments have no domain enum to filter on (no status/scope like
  // Roles), so `filters` stays empty and search carries the whole toolbar.
  // Search is the only filter here, so it also has to be the thing that puts
  // a chip in the ActiveFiltersBar — that bar renders nothing while `chips`
  // is empty, which would hide the result count and "Clear" entirely.
  const chips =
    search === ''
      ? []
      : [
          {
            key: 'search',
            label: `${t('departments.filters.search')}: ${search}`,
            onDelete: () => updateSearch(''),
          },
        ];

  const toolbarProps = {
    filters: [],
    search: { label: t('departments.filters.search'), value: search, onChange: updateSearch },
    chips,
    resultsLabel: t('departments.filters.resultsFound', { count: filteredDepartments.length }),
    clearLabel: t('departments.filters.clear'),
    onClearAll: clearFilters,
    gridSettings: {
      showColumnBorder,
      onShowColumnBorderChange: setShowColumnBorder,
      showCellBorder,
      onShowCellBorderChange: setShowCellBorder,
    },
    bulkAction: selectedCount > 0 && (
      <Button color="error" startIcon={<DeleteOutlineIcon />} onClick={handleDeleteSelected}>
        {t('departments.list.deleteSelected', { count: selectedCount })}
      </Button>
    ),
  };

  return (
    <BoundedPageLayout header={header}>
      <DataGridCard>
        {isMobile ? (
          <Box>
            <ListFilterToolbar {...toolbarProps} showGridControls={false} />
            {filteredDepartments.length === 0 ? (
              <EmptyState t={t} />
            ) : (
              <List disablePadding>
                {filteredDepartments
                  .slice(
                    paginationModel.page * paginationModel.pageSize,
                    (paginationModel.page + 1) * paginationModel.pageSize,
                  )
                  .map((department) => (
                    <ListItem
                      key={department.uuid}
                      divider
                      secondaryAction={
                        <RowActionsMenu
                          actions={[
                            { label: t('departments.actions.view'), icon: <VisibilityOutlinedIcon fontSize="small" />, onClick: () => navigate(`/departments/${department.uuid}`) },
                            { label: t('departments.actions.edit'), icon: <EditOutlinedIcon fontSize="small" />, onClick: () => navigate(`/departments/${department.uuid}/edit`) },
                            { label: t('departments.actions.delete'), icon: <DeleteOutlineIcon fontSize="small" />, color: 'error', onClick: () => handleDeleteOne(department) },
                          ]}
                        />
                      }
                    >
                      <Checkbox
                        edge="start"
                        checked={selectionModel.includes(department.uuid)}
                        onChange={(event) =>
                          setSelectionModel((prev) =>
                            event.target.checked ? [...prev, department.uuid] : prev.filter((id) => id !== department.uuid),
                          )
                        }
                        sx={{ mr: 1 }}
                      />
                      <ListItemText
                        primary={department.name}
                        secondary={
                          <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                              {department.internal_name}
                            </Typography>
                            {department.description && (
                              <Typography variant="caption" color="text.secondary">
                                {department.description}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(department.created_at, i18n.language)}
                            </Typography>
                          </Stack>
                        }
                        secondaryTypographyProps={{ component: 'div' }}
                      />
                    </ListItem>
                  ))}
              </List>
            )}
          </Box>
        ) : (
          <DataGrid
            // The description column wraps to a second line for longer text;
            // a fixed density-based row height clips it in compact mode, so
            // let each row size to its own content.
            getRowHeight={() => 'auto'}
            rows={filteredDepartments}
            columns={columns}
            getRowId={(row) => row.uuid}
            loading={isLoading}
            checkboxSelection
            disableRowSelectionOnClick
            rowSelectionModel={selectionModel}
            onRowSelectionModelChange={(model) => setSelectionModel(model)}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
            showColumnVerticalBorder={showColumnBorder}
            slots={{
              toolbar: ListFilterToolbar,
              noRowsOverlay: EmptyState,
              columnSelectorIcon: ViewColumnOutlinedIcon,
            }}
            slotProps={{
              toolbar: toolbarProps,
              noRowsOverlay: { t },
            }}
            localeText={{
              toolbarColumns: t('departments.filters.columns'),
              toolbarFilters: t('departments.filters.filters'),
            }}
            sx={{
              border: 0,
              ...(showCellBorder && {
                '& .MuiDataGrid-cell': { borderRight: '1px solid', borderColor: 'divider' },
              }),
            }}
          />
        )}
      </DataGridCard>

      <UndoSnackbar pending={pending} onUndo={undo} onDismiss={dismiss} undoLabel={t('departments.deleteUndo')} />

      <SuccessSnackbar open={Boolean(flashMessage)} message={flashMessage} onClose={() => setFlashMessage('')} />
    </BoundedPageLayout>
  );
}

function EmptyState({ t }) {
  return (
    <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ py: 6, textAlign: 'center' }}>
      <Typography variant="body1">{t('departments.list.empty')}</Typography>
      <Typography variant="body2" color="text.secondary">
        {t('departments.list.emptyHint')}
      </Typography>
    </Stack>
  );
}
